<template>
  <div class="chat-page">
    <!-- 添加水印 -->
    <Watermark />

    <div class="chat-container">
      <!-- 问卷部分，始终显示 -->
      <div>
        <div class="message ai-message">
          <div class="message-content">
            Hi~朋友，我是您的AI助理，请问您想编写的标准核心思想是？
          </div>
        </div>

        <div class="input-area">
          <demand-survey ref="demandSurvey" @validation-change="handleValidationChange" />
          <el-button type="primary" round class="submit-button" :disabled="!isSurveyValid" @click="handleSubmit">
            下一步
          </el-button>
        </div>

        <!-- 标题选择界面 -->
        <div v-if="showTitleSelection" class="title-selection">
          <div class="message ai-message">
            <div class="message-content">
              请从以下备选标题中选择一个，或者自定义您的标题：
            </div>
          </div>

          <div class="title-options">
            <div v-for="(title, index) in standardTitles" :key="index" class="title-option"
              :class="{ 'selected': selectedTitleIndex === index }" @click="selectTitle(index)">
              <template v-if="selectedTitleIndex === index">
                <div class="editable-title-container">
                  <i class="el-icon-edit edit-icon"></i>
                  <el-input v-model="customTitle" placeholder="请输入自定义标题" class="title-edit-field" @click.native.stop
                    ref="titleInput"></el-input>
                </div>
              </template>
              <template v-else>
                {{ title }}
              </template>
            </div>

            <!-- 上传参考文档 -->
            <el-upload class="upload-component" action="/api/common/upload" :on-success="handleUploadSuccess"
              :on-error="handleUploadError" :before-upload="beforeUpload" :on-remove="handleRemove" :limit="1"
              :file-list="fileList" drag>
              <div class="upload-area" v-if="fileList.length === 0">
                <img src="@/assets/images/icon-title-file.png" class="upload-icon" alt="上传文件" />
                <div class="upload-text">请上传内容参考文档，可直接拖进此区域（可上传一篇）</div>
              </div>
              <div v-else class="upload-file-item">
                <img src="@/assets/images/icon-wps.png" class="file-icon" alt="WPS文件" />
                <div class="file-name">{{ fileList[0].name }}</div>
                <i class="el-icon-delete delete-icon" @click.stop="handleRemove(fileList[0])"></i>
              </div>
            </el-upload>

            <el-button type="primary" round class="submit-button" :disabled="!isTitleSelected"
              @click="handleTitleSubmit">
              生成标准大纲
            </el-button>
          </div>
        </div>
      </div>

      <!-- 大纲部分，条件显示 -->
      <div v-if="showOutline" class="outline-section">
        <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
          <div class="message ai-message" v-if="message.type === 'ai'">
            <div class="message-content">
              {{ message.content }}
            </div>
          </div>
          <div class="message user-message" v-else-if="message.type === 'user'">
            <div class="message-content">
              {{ message.content }}
            </div>
          </div>
          <div class="standard-preview" v-else-if="message.type === 'standard'">
            <div class="standard-header">
              <div class="logo">
                <img src="../assets/images/logo.png" alt="Aibz Logo">
              </div>
            </div>
            <div class="standard-content">
              <h2>{{ message.title }}</h2>
              <div class="standard-text">{{ message.content }}</div>
            </div>
          </div>
          <div v-else-if="message.type === 'docModule'" class="doc-module" @click="openDocPreview(message)">
            <img :src="require(`@/assets/images/${message.icon}`)" class="doc-icon" alt="文档图标" />
            <div class="doc-title">{{ message.title }}</div>
          </div>
          <div v-else-if="message.type === 'document'" class="document-content">
            <h3>{{ message.title }}</h3>
            <div v-html="message.content"></div>
            <el-button type="primary" @click="downloadDocument(message.downloadUrl)">
              下载{{ message.title }}
            </el-button>
          </div>
          <Outline v-else-if="message.type === 'outline'" @generate="handleGenerate" :outlineData="outlineData" />
        </div>
      </div>
    </div>

    <!-- 回到顶部组件 -->
    <back-to-top />

    <!-- 标准生成加载进度条弹窗 -->
    <el-dialog :visible.sync="loadingVisible" :show-close="false" :close-on-click-modal="false"
      :close-on-press-escape="false" width="500px" center class="loading-dialog">
      <div class="loading-content">
        <div class="loading-circle">
          <div class="circle-bg"
            :style="{ backgroundImage: 'url(' + require('@/assets/images/img-loading-bg.png') + ')' }">
            <div class="circle-progress">
              <el-progress type="circle" :percentage="Math.round(genProgress)" :width="182" :stroke-width="6"
                stroke-linecap="round" color="#5C3D93" :show-text="false"></el-progress>
            </div>
            <div class="circle-text">
              <div class="progress-percentage">{{ Math.round(genProgress) }}%</div>
              <div class="loading-message">{{ loadingMessage }}</div>
            </div>
          </div>
        </div>
        <div class="loading-tips">{{ loadingTips }}</div>
      </div>
    </el-dialog>

    <!-- 大纲生成加载进度条弹窗 -->
    <el-dialog :visible.sync="outlineLoadingVisible" :show-close="false" :close-on-click-modal="false"
      :close-on-press-escape="false" width="500px" center class="loading-dialog">
      <div class="loading-content">
        <div class="loading-circle">
          <div class="circle-bg"
            :style="{ backgroundImage: 'url(' + require('@/assets/images/img-loading-bg.png') + ')' }">
            <div class="circle-progress">
              <el-progress type="circle" :percentage="Math.round(outlineProgress)" :width="182" :stroke-width="6"
                stroke-linecap="round" color="#5C3D93" :show-text="false"></el-progress>
            </div>
            <div class="circle-text">
              <div class="progress-percentage">{{ Math.round(outlineProgress) }}%</div>
              <div class="loading-message">正在生成大纲</div>
            </div>
          </div>
        </div>
        <div class="loading-tips">{{ outlineLoadingTips }}</div>
      </div>
    </el-dialog>

    <!-- 标准文档查看对话框 -->
    <el-dialog 
      title="标准文档" 
      :visible.sync="standardDocVisible" 
      width="70%" 
      :close-on-click-modal="false">
      <div class="html-content" v-html="currentDocContent"></div>
      <span slot="footer" class="dialog-footer">
        <!-- <el-button @click="editDocOnline" type="success" :loading="uploading">在线编辑</el-button> -->
        <el-button @click="downloadDocument(currentDocDownloadUrl)" type="primary">下载文档</el-button>
        <el-button @click="standardDocVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 编制说明查看对话框 -->
    <el-dialog 
      title="编制说明" 
      :visible.sync="compileInstructionsVisible" 
      width="70%" 
      :close-on-click-modal="false">
      <div class="html-content" v-html="currentDocContent"></div>
      <span slot="footer" class="dialog-footer">
        <!-- <el-button @click="editDocOnline" type="success" :loading="uploading">在线编辑</el-button> -->
        <el-button @click="downloadDocument(currentDocDownloadUrl)" type="primary">下载文档</el-button>
        <el-button @click="compileInstructionsVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Outline from '../components/Outline.vue'
import DemandSurvey from '../components/DemandSurvey.vue'
import Watermark from '@/components/Watermark.vue'
import BackToTop from '@/components/BackToTop.vue'
import data from '@/utils/data'
import { post, get } from '@/utils/fetch'

export default {
  name: 'ChatPage',
  components: {
    Outline,
    DemandSurvey,
    Watermark,
    BackToTop
  },
  data() {
    return {
      userInput: '',
      showOutline: false,
      showTitleSelection: false,
      showStandard: false,
      messages: [],
      selectedValue: '',
      groupOptions: data.groupData,
      outlineData: [],
      demandSurveyData: {},
      isSurveyValid: false,
      loadingVisible: false,
      genProgress: 0,
      progressTimer: null,
      outlineLoadingVisible: false,
      outlineProgress: 0,
      outlineProgressTimer: null,
      loadingMessage: '正在生成标准',
      loadingTips: '请勿刷新页面',
      outlineLoadingTips: '请勿刷新页面，预计完成时间：2分钟',
      progressCheckTimer: null,
      failedApiCount: 0, // 连续API失败次数
      standardTitles: [],
      selectedTitleIndex: -1,
      isCustomTitle: false,
      customTitle: '',
      selectedTitle: '',
      referUrl: '',
      fileList: [],
      // 新增属性
      standardDocVisible: false,
      compileInstructionsVisible: false,
      currentDocContent: '',
      currentDocDownloadUrl: '',
      currentDocType: '',
      uploading: false
    }
  },
  computed: {
    isTitleSelected() {
      return this.selectedTitleIndex >= 0 && this.customTitle.trim() !== '';
    }
  },
  methods: {
    async handleSubmit() {
      // 获取完整的问卷数据
      const surveyData = this.$refs.demandSurvey.getSurveyData();

      try {
        // 显示加载状态
        this.loadingVisible = true;
        this.genProgress = 0;
        this.loadingMessage = '正在生成标题';
        this.loadingTips = '请勿刷新页面';

        // 设置简单进度条
        this.progressTimer = setInterval(() => {
          if (this.genProgress < 90) {
            this.genProgress += 5;
          }
        }, 500);

        // 调用标题生成接口
        const response = await post('/api/aibz/standardName/gen', {
          ...surveyData
        });

        // 清除进度条
        clearInterval(this.progressTimer);
        this.genProgress = 100;
        this.loadingVisible = false;

        // 保存问卷数据和标题数据
        this.demandSurveyData = surveyData;
        this.standardTitles = response.data.standardNames || [];

        // 显示标题选择界面
        this.showTitleSelection = true;

        // 滚动到标题选择区域
        this.$nextTick(() => {
          this.scrollToLatestMessage();
        });
      } catch (error) {
        // 清除进度条
        clearInterval(this.progressTimer);
        this.loadingVisible = false;

        console.error('获取标题数据失败：', error);
        this.$message.error(error || '获取标题数据失败，请重试');
      }
    },

    selectTitle(index) {
      // 记录先前选中的索引
      const previousIndex = this.selectedTitleIndex;
      // 更新选中索引
      this.selectedTitleIndex = index;
      this.isCustomTitle = false;

      // 如果切换了标题，重置customTitle为新选中标题的内容
      if (previousIndex !== index) {
        this.customTitle = this.standardTitles[index];
      }

      this.selectedTitle = this.customTitle;

      // 聚焦输入框
      this.$nextTick(() => {
        if (this.$refs.titleInput && this.$refs.titleInput.$el) {
          this.$refs.titleInput.focus();
        }
      });
    },

    // 上传相关方法
    beforeUpload(file) {
      // 可以在这里添加文件类型、大小等限制
      const isValidType = true; // 根据需要添加类型限制
      const isLt20M = file.size / 1024 / 1024 < 20;

      if (!isValidType) {
        this.$message.error('上传文件格式不正确!');
        return false;
      }
      if (!isLt20M) {
        this.$message.error('上传文件大小不能超过 20MB!');
        return false;
      }
      return true;
    },

    handleUploadSuccess(response, file, fileList) {
      if (response && response.status === 'success') {
        this.referUrl = response.url || '';
        this.fileList = fileList.map(item => {
          // 确保文件对象包含所需属性
          return {
            name: item.name,
            url: item.url || response.url
          };
        });
        this.$message.success('参考文档上传成功');
      } else {
        this.fileList = [];
        this.referUrl = '';
        this.$message.error('参考文档上传失败');
      }
    },

    handleUploadError() {
      this.fileList = [];
      this.referUrl = '';
      this.$message.error('上传文件失败，请重试');
    },

    handleRemove() {
      this.fileList = [];
      this.referUrl = '';
    },

    async handleTitleSubmit() {
      // 获取选中的标题
      this.selectedTitle = this.customTitle;

      // 显示进度条对话框
      this.outlineLoadingVisible = true;
      this.outlineProgress = 0;
      this.outlineLoadingTips = '请勿刷新页面';

      // 设置进度条，2分钟内完成
      const totalTime = 2 * 60 * 1000; // 2分钟（毫秒）
      const interval = 2000; // 每2秒更新一次
      const incrementPerStep = 100 / (totalTime / interval);

      this.outlineProgressTimer = setInterval(() => {
        this.outlineProgress = Math.min(99, Math.round(this.outlineProgress + incrementPerStep));
      }, interval);

      try {
        const response = await post('/api/aibz/catalog', {
          // 将完整的问卷数据和选中的标题传递给后端
          ...this.demandSurveyData,
          standardName: this.selectedTitle,
          referUrl: this.referUrl
        });

        // 清除定时器并关闭进度条对话框
        clearInterval(this.outlineProgressTimer);
        this.outlineProgress = 100;

        // 短暂延迟后关闭对话框，让用户看到100%完成
        setTimeout(() => {
          this.outlineLoadingVisible = false;
        }, 500);

        this.outlineData = response.data.outlines;
        this.showOutline = true;
        this.messages = [
          { type: 'user', content: `标准类型：${this.demandSurveyData.standardType}` },
          { type: 'user', content: `已选择标题：${this.selectedTitle}` },
          { type: 'ai', content: '让我帮您生成一下大纲，您看看如何？' },
          { type: 'outline', data: this.outlineData }
        ];

        // 滚动到最新消息
        this.$nextTick(() => {
          this.scrollToLatestMessage();
        });
      } catch (error) {
        // 清除定时器并关闭进度条对话框
        clearInterval(this.outlineProgressTimer);
        this.outlineLoadingVisible = false;

        console.error('获取大纲数据失败：', error);
        this.$message.error(error || '获取大纲数据失败，请重试');
      }
    },
    async handleGenerate() {
      // 获取完整的问卷数据
      const surveyData = this.demandSurveyData;

      // 显示进度条对话框
      this.loadingVisible = true
      this.genProgress = 0
      this.loadingMessage = '正在生成标准'
      this.loadingTips = '请勿刷新页面，生成的文档可在历史记录页再次查看'
      this.failedApiCount = 0 // 重置失败计数

      try {
        // 调用生成接口获取docId
        const genResponse = await post('/api/aibz/content/gen', {
          ...surveyData,
          standardName: this.selectedTitle,
          referUrl: this.referUrl,
          outlines: this.outlineData
        })

        const { docId } = genResponse

        // 定时查询进度
                  const checkProgress = async () => {
          try {
            const progressResponse = await get('/api/aibz/progress', { id: docId })
            const { progress, progressDesc, status } = progressResponse

            // API调用成功，重置失败计数
            this.failedApiCount = 0

            // 更新进度
            this.genProgress = progress
            this.loadingMessage = progressDesc || '正在生成标准'

            if (status === 'generating') {
              // 继续查询进度
              this.progressCheckTimer = setTimeout(checkProgress, 10000)
            } else if (status === 'completed') {
              // 完成生成，获取文档
              const { bzsm, bzsmDocUrl, html, htmlDocUrl } = progressResponse

              // 关闭进度条对话框
              this.loadingVisible = false

              this.showStandard = true
              this.messages.push(
                { type: 'ai', content: '已为您生成标准内容，请查看：' },
                {
                  type: 'docModule',
                  title: '编制说明文档',
                  icon: 'icon-wps-blue.png',
                  content: bzsm,
                  downloadUrl: bzsmDocUrl
                },
                {
                  type: 'docModule',
                  title: '标准文档',
                  icon: 'icon-wps.png',
                  content: html,
                  downloadUrl: htmlDocUrl
                }
              )

              // 滚动到最新消息
              this.$nextTick(() => {
                this.scrollToLatestMessage();
              });
            } else if (status === 'failed') {
              // 生成失败
              this.loadingVisible = false
              this.$message.error('生成标准失败，请重试')
            }
          } catch (error) {
            console.error('获取进度失败：', error)

            // 增加失败计数
            this.failedApiCount++

            if (this.failedApiCount >= 5) {
              // 连续失败5次，停止轮询
              this.loadingVisible = false
              this.$message.error('标准生成失败，请重试')
            } else {
              // 低于5次失败，继续轮询
              console.log(`API调用失败 ${this.failedApiCount} 次，继续轮询...`)
              this.progressCheckTimer = setTimeout(checkProgress, 10000)
            }
          }
        }

        // 开始查询进度
        checkProgress()
      } catch (error) {
        // 生成请求失败
        this.loadingVisible = false
        console.error('生成标准失败：', error)
        this.$message.error(error || '生成标准失败，请重试')
      }
    },
    // 滚动到最新消息
    scrollToLatestMessage() {
      setTimeout(() => {
        const messageElements = document.querySelectorAll('.message-content');
        if (messageElements.length > 0) {
          const lastMessage = messageElements[messageElements.length - 1];
          // 使用start而不是center，并在滚动后额外向上偏移，防止被导航栏遮挡
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    },
    handleGroupChange(e) {
      this.selectedValue = e[e.length - 1]
    },
    downloadDocument(url) {
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', '')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleValidationChange(isValid) {
      // 更新问卷验证状态
      this.isSurveyValid = isValid;
      console.log('问卷验证状态已更新:', isValid);
    },
    // 打开文档预览对话框
    openDocPreview(docModule) {
      this.currentDocContent = docModule.content
      this.currentDocDownloadUrl = docModule.downloadUrl
      this.currentDocType = docModule.title

      if (docModule.title.includes('编制说明')) {
        this.compileInstructionsVisible = true
      } else {
        this.standardDocVisible = true
      }
    },

    // 在线编辑文档
    editDocOnline() {
      this.uploading = true
      // 这里可以添加在线编辑的逻辑，如打开Office Online或其他在线编辑器
      setTimeout(() => {
        this.uploading = false
        this.$message.info('在线编辑功能即将上线')
      }, 1000)
    }
  },
  beforeDestroy() {
    // 组件销毁前清除定时器
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }
    if (this.outlineProgressTimer) {
      clearInterval(this.outlineProgressTimer);
    }
    if (this.progressCheckTimer) {
      clearTimeout(this.progressCheckTimer);
    }
  }
}
</script>

<style scoped>
.chat-page {
  min-height: calc(100vh - 60px);
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
}

.chat-container {
  flex: 1;
  padding: 40px;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* 大纲部分样式 */
.outline-section {
  width: 100%;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px dashed #e0e0e0;
}

.progress-bar {
  margin-bottom: 20px;
  position: relative;
}

.progress-text {
  position: absolute;
  right: 0;
  top: -20px;
  font-size: 14px;
  color: #7e57c2;
}

.message {
  margin-bottom: 25px;
  display: flex;
  width: 100%;
}

.ai-message {
  justify-content: flex-start;
}

.user-message {
  justify-content: flex-end;
}

.message-content {
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.5;
  max-width: 80%;
  white-space: pre-wrap;
}

.ai-message .message-content {
  background-color: #E4E0FF;
  font-family: AlibabaPuHuiTi_2_55_Regular;
  font-size: 16px;
  color: #5C3D93;
  line-height: 22px;
  text-align: center;
  font-style: normal;
  position: relative;
  border-radius: 0px 27px 27px 27px;
}

.ai-message .message-content::before {
  content: none;
}

.user-message .message-content {
  background-color: #7e57c2;
  color: white;
  position: relative;
  margin-left: auto;
}

.user-message .message-content::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 16px;
  width: 12px;
  height: 12px;
  background-color: #7e57c2;
  transform: rotate(45deg);
}

.input-area {
  padding-bottom: 40px;
  background-color: white;
  /* padding: 24px; */
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.custom-textarea {
  margin-bottom: 20px;
}

.group-options {
  margin-bottom: 20px;
  width: 100%;
}

.custom-textarea>>>.el-textarea__inner {
  border-radius: 8px;
  padding: 15px;
  font-size: 16px;
  border-color: #e0e0e0;
  resize: none;
}

.custom-textarea>>>.el-textarea__inner:focus {
  border-color: #7e57c2;
}

.submit-button {
  display: block;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 20px;
  font-size: 18px;
  height: 52px;
  min-width: 159px;
  line-height: 52px;
  background: linear-gradient(270deg, #8F9EFF 0%, #4E1AD6 100%);
  border-radius: 43px;
  border: none;
}

.submit-button:disabled {
  background: #c0c4cc;
}

.standard-preview {
  background: white;
  border-radius: 12px;
  margin: 20px 0;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.standard-header {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ebeef5;
}

.standard-tabs {
  display: flex;
  padding: 0 20px;
  border-bottom: 1px solid #ebeef5;
}

.tab-item {
  padding: 12px 24px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
}

.tab-item.active {
  color: #7e57c2;
  border-bottom: 2px solid #7e57c2;
}

.standard-content {
  padding: 20px;
}

.standard-content h2 {
  font-size: 18px;
  color: #303133;
  margin-bottom: 20px;
  font-weight: normal;
}

.standard-text {
  color: #606266;
  line-height: 1.8;
  white-space: pre-line;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.document-content {
  background: white;
  border-radius: 12px;
  margin: 20px 0;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.document-content h3 {
  font-size: 18px;
  color: #303133;
  margin-bottom: 20px;
  font-weight: normal;
}

.document-content div {
  color: #606266;
  line-height: 1.8;
}

.standard-header .logo {
  display: flex;
  align-items: center;
}

.standard-header .logo img {
  height: 40px;
  width: auto;
  object-fit: contain;
  margin-right: 10px;
}

.standard-header .logo-text {
  font-size: 20px;
  font-weight: bold;
  color: #7e57c2;
}

/* 标题选择样式 */
.title-selection {
  margin: 30px 0;
}

.title-options {
  background-color: white;
  padding: 24px;
  padding-bottom: 70px;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.title-option {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  border: 1px solid #E4E0FF;
  transition: all 0.3s ease;
  color: #BCA7E1;
}

.title-option:hover {
  border-color: #7e57c2;
  color: #7e57c2;
}

.title-option.selected {
  border-color: #7e57c2;
  background-color: #f5efff;
  color: #7e57c2;
  position: relative;
}

.custom-option {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.custom-option::before {
  content: "+";
  margin-right: 8px;
  font-size: 18px;
}

.custom-title-input {
  margin: 12px 0 20px;
}

.custom-title-field {
  width: 100%;
}

.custom-title-field>>>.el-input__inner {
  border-radius: 8px;
  height: 45px;
  font-size: 15px;
}

/* 标题编辑样式 */
.title-edit-field {
  margin-left: 10px;
  width: calc(100% - 40px);
}

.title-edit-field>>>.el-input__inner {
  text-indent: 10px;
  border-radius: 8px;
  height: 45px;
  font-size: 15px;
  border: none;
  background: transparent;
  padding: 0;
  border-bottom: 1px dashed #7e57c2;
  transition: all 0.3s ease;
}

.title-edit-field>>>.el-input__inner:hover,
.title-edit-field>>>.el-input__inner:focus {
  border-bottom: 2px solid #7e57c2;
  background-color: rgba(126, 87, 194, 0.05);
}

/* 参考文档上传样式 */
.reference-doc-upload {
  margin: 20px 0;
  padding: 16px;
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
  background-color: #f8f9fa;
}

.upload-title {
  font-weight: 500;
  margin-bottom: 12px;
  color: #606266;
}

.upload-component {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #F8F7FF;
  font-family: AlibabaPuHuiTi_2_55_Regular;
  font-size: 14px;
  color: #BCA7E1;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  margin-bottom: 20px;
}

.el-upload__tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 30px;
  padding: 20px 0;
}

.el-icon-upload {
  font-size: 48px;
  color: #5C3D93;
  margin-bottom: 10px;
}

.upload-icon {
  width: 36px;
  height: 40px;
  margin-bottom: 10px;
}

.upload-text {
  font-size: 14px;
  color: #BCA7E1;
  margin-bottom: 15px;
  text-align: center;
}

.upload-component>>>.el-upload-dragger {
  width: 100%;
  border: 2px dashed #E4E0FF;
  background-color: #F8F7FF;
  border-radius: 8px;
}

.upload-component>>>.el-upload-dragger:hover {
  border-color: #5C3D93;
}

.upload-component>>>.el-upload-dragger.is-dragover {
  background-color: rgba(126, 87, 194, 0.1);
  border-color: #5C3D93;
}

/* 上传文件成功后的样式 */
.upload-file-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 15px;
  justify-content: center;
  gap: 20px;
  margin-top: 50px;
}

.file-icon {
  width: 34px;
  height: 34px;
  margin-bottom: 8px;
}

.file-name {
  font-family: PingFangSC, PingFang SC;
  font-weight: 400;
  font-size: 14px;
  color: #5C3D93;
  line-height: 20px;
  text-align: center;
  font-style: normal;
  word-break: break-all;
  max-width: 100%;
  margin-bottom: 5px;
}

.delete-icon {
  color: #BCA7E1;
  cursor: pointer;
  font-size: 16px;
  margin-top: -5px;
}

.delete-icon:hover {
  color: #5C3D93;
}

/* 隐藏默认的上传文件列表样式 */
.upload-component>>>.el-upload-list__item {
  display: none;
}

/* 加载对话框样式 */
.loading-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(3px);
}

.loading-dialog>>>.el-dialog {
  margin: 0 !important;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: none;
  background: transparent;
}

.loading-dialog>>>.el-dialog__body {
  padding: 0;
  background: transparent;
}

.loading-dialog>>>.el-dialog__header {
  display: none;
}

.loading-dialog>>>.el-dialog__wrapper {
  background: transparent;
  overflow: hidden;
}

.loading-dialog>>>.v-modal {
  display: none;
}

.loading-content {
  text-align: center;
  background: transparent;
}

.loading-circle {
  width: 300px;
  height: 300px;
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-bg {
  width: 100%;
  height: 100%;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-progress {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

/* 自定义el-progress样式 */
.circle-progress>>>.el-progress__circle {
  transform: scale(0.9);
}

.circle-progress>>>.el-progress__text {
  display: none !important;
}

/* 隐藏进度条的灰色轨道 */
.circle-progress>>>.el-progress__circle .el-progress-circle__track {
  stroke: transparent;
}

.circle-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  background: transparent;
}

.progress-percentage {
  font-size: 36px;
  font-weight: bold;
  color: #5C3D93;
  line-height: 1.2;
}

.loading-message {
  font-size: 12px;
  color: #333;
  margin-top: 5px;
}

.loading-tips {
  font-size: 14px;
  color: #666;
  margin-top: 15px;
  font-style: italic;
}

/* 文档模块样式 */
.doc-module {
  display: flex;
  align-items: center;
  width: 50%;
  padding: 20px;
  background: white;
  border-radius: 12px;
  margin: 20px 0;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.doc-module:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
}

.doc-icon {
  width: 40px;
  height: 40px;
  margin-right: 15px;
}

.doc-title {
  font-size: 16px;
  color: #5C3D93;
  font-weight: 500;
}

/* 文档预览对话框样式 */
.html-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
</style>